import { useMyMaterialRequests } from '../hooks/useMyMaterialRequests'
import React, { useState, useEffect, useMemo } from 'react'
import { useQueryClient } from 'react-query'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { 
  LayoutDashboard, 
  Package, 
  Warehouse, 
  MapPin, 
  Truck, 
  Move, 
  Bell,
  Menu,
  X,
  LogOut,
  Users,
  ArrowRightLeft,
  ShoppingCart,
  PackagePlus,
  ClipboardList,
  ListChecks,
  Building2,
  FileText,
  Briefcase
} from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { useSSE } from '../hooks/useSSE'
import { useMobileWebProfile } from '../hooks/useMobileWebProfile'
import { canAccessPurchaseOrders, canAccessDefectSheets, canAccessFieldJobs, canAccessFieldHours } from '../utils/permissions'
import {
  MOBILE_PROFILES,
  getMobileAppProfile,
  canAccessDefectSheetsMobile,
  canAccessQuotationsMobile,
  hasMobilePermission,
} from '../utils/mobileProfile'
import MobileWebRouteGuard from './MobileWebRouteGuard'
import UserAvatar from './UserAvatar'
import { cronSyncAPI } from '../lib/api'
import { prefetchRouteChunk } from '../utils/routePrefetch'
import { clearClientsListScrollIntent, scrollWindowYInstant } from '../utils/clientsListScrollSession'
import { applyTaskStatusEvent } from '../utils/defectSheetTaskStatus'

const navPrefetchProps = (href) => ({
  onMouseEnter: () => prefetchRouteChunk(href),
  onFocus: () => prefetchRouteChunk(href),
})

/** Red dot on sidebar icon (parent items). */
function SidebarNavDot() {
  return (
    <div
      className="pointer-events-none absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-red-500 animate-pulse"
      aria-hidden
    />
  )
}

/** Red dot on submenu row (right side). */
function SidebarSubmenuDot() {
  return (
    <span
      className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full bg-red-500 animate-pulse"
      aria-hidden
    />
  )
}

function stockSubmenuHrefHasNotification(href, flags) {
  const { hasNewMovements, hasNewMaterialRequests, hasNewPurchaseOrders } = flags
  if (href === '/material-requests') return hasNewMaterialRequests
  if (href === '/movements') return hasNewMovements
  if (href === '/purchase-orders') return hasNewPurchaseOrders
  return false
}

function isJobsSubmenuActive (href, pathname) {
  if (href === '/jobs/dashboard') {
    return pathname === '/jobs/dashboard'
  }
  if (href === '/jobs') {
    if (pathname === '/jobs') return true
    if (/^\/jobs\/\d+(\/|$)/.test(pathname) && !/^\/jobs\/\d+\/defect-sheet/.test(pathname)) {
      return true
    }
    return false
  }
  if (href === '/jobs/defect-sheets') {
    return (
      pathname === '/jobs/defect-sheets'
      || pathname.startsWith('/jobs/defect-sheets/')
      || /^\/jobs\/\d+\/defect-sheet/.test(pathname)
    )
  }
  if (href === '/jobs/clients') {
    return pathname === '/jobs/clients' || pathname.startsWith('/jobs/clients/')
  }
  if (href === '/jobs/quotations') {
    return pathname === '/jobs/quotations' || pathname.startsWith('/jobs/quotations/')
  }
  if (href === '/jobs/stock-usage') {
    return pathname === '/jobs/stock-usage' || pathname.startsWith('/jobs/stock-usage/')
  }
  if (href === '/jobs/time-tracking') {
    return pathname === '/jobs/time-tracking' || pathname.startsWith('/jobs/time-tracking/')
  }
  return pathname === href
}

function isSubmenuItemActive (parentName, href, pathname) {
  if (parentName === 'Jobs') {
    return isJobsSubmenuActive(href, pathname)
  }
  if (parentName === 'Stock') {
    if (href === '/stock/dashboard') {
      return pathname === '/stock/dashboard'
    }
    if (href === '/stock') {
      const onStockDashboard = pathname === '/stock/dashboard'
      return !onStockDashboard && (pathname === '/stock' || pathname.startsWith('/stock/'))
    }
  }
  return pathname === href || pathname.startsWith(`${href}/`)
}

function submenuSectionActive (item, pathname) {
  if (!item.submenu?.length) return false
  return item.submenu.some((sub) => isSubmenuItemActive(item.name, sub.href, pathname))
}

function jobsSubmenuHrefHasNotification(href) {
  return href === '/jobs/defect-sheets'
}

/** Clear the Defect sheets notification after that section is opened. */
function pathClearsJobsNavDot(pathname) {
  if (pathname === '/jobs/defect-sheets') return true
  if (/^\/jobs\/defect-sheets\/\d+/.test(pathname)) return true
  if (/^\/jobs\/\d+\/defect-sheet/.test(pathname)) return true
  return false
}

/** Clients list: always top + no stale scroll-restore from a previous card navigation. */
const onJobsSubmenuLinkClick = (href) => {
  if (href !== '/jobs/clients') return
  clearClientsListScrollIntent()
  scrollWindowYInstant(0)
}

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [hasNewMovements, setHasNewMovements] = useState(false)
  const [hasNewMaterialRequests, setHasNewMaterialRequests] = useState(false)
  const [hasNewAlerts, setHasNewAlerts] = useState(false)
  const [hasNewPurchaseOrders, setHasNewPurchaseOrders] = useState(false)
  /** Admin: Jobs section — set via window event until backend emits job-specific realtime. */
  const [hasNewJobsActivity, setHasNewJobsActivity] = useState(false)
  const [expandedMenus, setExpandedMenus] = useState({})
  const [syncStatus, setSyncStatus] = useState(null)
  const { user, logout } = useAuth()
  const { appliesMobileProfile, roleLabel: mobileRoleLabel } = useMobileWebProfile()
  const displayRoleLabel = appliesMobileProfile
    ? mobileRoleLabel
    : (user?.role_id === 1 ? 'Admin' : 'User')
  const { isConnected } = useSSE()
  const queryClient = useQueryClient()
  const location = useLocation()
  const navigate = useNavigate()
  const pickupReadyQuery = useMyMaterialRequests(user, true, true)
  const hasPickupReady = (pickupReadyQuery.data?.data?.requests || [])
    .some((request) => request.status === 'separated')
  const [menuKey, setMenuKey] = useState(0) // Force re-render when user changes
  const [forceUpdate, setForceUpdate] = useState(0)
  
  // Force menu update when user changes (especially after login)
  useEffect(() => {
    // Check localStorage for user data as fallback
    const storedUser = localStorage.getItem('user')
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser)
        // If user in state doesn't have permissions but stored user does, force update
        if (parsedUser.permission_codes || parsedUser.permissions) {
          setMenuKey(prev => prev + 1)
          setForceUpdate(prev => prev + 1)
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
  }, [user?.id, user?.permission_codes, user?.permissions])
  
  // One delayed check after login so permissions from localStorage can sync to React state (no 500ms polling)
  useEffect(() => {
    if (!user) return
    const t = setTimeout(() => {
      const storedUser = localStorage.getItem('user')
      if (!storedUser) return
      try {
        const parsedUser = JSON.parse(storedUser)
        const hasStoredPermissions = parsedUser.permission_codes || parsedUser.permissions
        const hasStatePermissions = user.permission_codes || user.permissions
        if (hasStoredPermissions && !hasStatePermissions) {
          setMenuKey((prev) => prev + 1)
          setForceUpdate((prev) => prev + 1)
        }
      } catch (_) {
        /* ignore */
      }
    }, 600)
    return () => clearTimeout(t)
  }, [user])
  
  // Debug location changes
  React.useEffect(() => {
    // Location change tracking removed
  }, [location.pathname]);
  

  // Different navigation for painters vs admins
  // Use useMemo to recalculate when user changes (important for login state updates)
  // Also check localStorage as fallback for permissions
  const painterNav = useMemo(() => {
    // Get user data - prefer state, fallback to localStorage
    let currentUser = user
    if (!currentUser || (!currentUser.permission_codes && !currentUser.permissions)) {
      try {
        const storedUser = localStorage.getItem('user')
        if (storedUser) {
          const parsedUser = JSON.parse(storedUser)
          if (parsedUser.permission_codes || parsedUser.permissions) {
            currentUser = parsedUser
          }
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
    
    const navItems = [
      { name: 'My Dashboard', href: '/painter/dashboard', icon: LayoutDashboard },
      { name: 'Defect Sheets', href: '/worker/defect-sheets', icon: ListChecks, requiresPermission: 'defect_sheets' },
      { name: 'Transfer Stock', href: '/painter/transfer', icon: ArrowRightLeft },
      { name: 'Check Stock', href: '/painter/stock', icon: Warehouse },
      { name: 'Request Material', href: '/painter/request', icon: ShoppingCart },
      { name: 'My Requests', href: '/painter/my-requests', icon: ClipboardList },
      { name: 'Monthly Restock', href: '/painter/monthly-restock', icon: PackagePlus },
      { name: 'My Jobs', href: '/worker/jobs', icon: Briefcase, requiresPermission: 'field_jobs' },
      { name: 'My Hours', href: '/worker/hours', icon: ClipboardList, requiresPermission: 'field_hours' },
      { name: 'Purchase Orders', href: '/painter/purchase-orders', icon: FileText, requiresPermission: 'purchase_orders' },
      { name: 'Movements', href: '/painter/movements', icon: Move },
      { name: 'Alerts', href: '/painter/alerts', icon: Bell },
    ];
    
    return navItems.filter(item => {
      if (item.requiresPermission && currentUser) {
        const checks = {
          purchase_orders: canAccessPurchaseOrders,
          defect_sheets: canAccessDefectSheets,
          field_jobs: canAccessFieldJobs,
          field_hours: canAccessFieldHours,
        }
        const check = checks[item.requiresPermission]
        return check ? check(currentUser) : false
      }
      return true;
    });
  }, [user, forceUpdate]);

  const mobileAdminNav = useMemo(() => {
    if (!appliesMobileProfile) return null

    let currentUser = user
    if (!currentUser || (!currentUser.permission_codes && !currentUser.permissions)) {
      try {
        const storedUser = localStorage.getItem('user')
        if (storedUser) {
          const parsedUser = JSON.parse(storedUser)
          if (parsedUser.permission_codes || parsedUser.permissions) {
            currentUser = parsedUser
          }
        }
      } catch (e) {
        /* ignore */
      }
    }

    const profile = getMobileAppProfile(currentUser)

    if (profile === MOBILE_PROFILES.NONE) {
      return [{ name: 'Home', href: '/mobile/dashboard', icon: LayoutDashboard }]
    }

    if (profile === MOBILE_PROFILES.SALES) {
      const items = [{ name: 'Dashboard', href: '/mobile/dashboard', icon: LayoutDashboard }]
      if (canAccessQuotationsMobile(currentUser)) {
        items.push({ name: 'Quotations', href: '/jobs/quotations', icon: FileText })
      }
      if (canAccessDefectSheetsMobile(currentUser)) {
        items.push({ name: 'Defect Sheets', href: '/jobs/defect-sheets', icon: ListChecks })
      }
      return items
    }

    const items = [
      { name: 'My Dashboard', href: '/painter/dashboard', icon: LayoutDashboard },
      { name: 'My Stock', href: '/painter/stock', icon: Warehouse },
      { name: 'Transfer Stock', href: '/painter/transfer', icon: ArrowRightLeft },
      { name: 'Request Material', href: '/painter/request', icon: ShoppingCart },
      { name: 'My Requests', href: '/painter/my-requests', icon: ClipboardList },
      { name: 'Movements', href: '/painter/movements', icon: Move },
      { name: 'Alerts', href: '/painter/alerts', icon: Bell },
    ]

    if (hasMobilePermission(currentUser, 'field_jobs')) {
      items.splice(1, 0, { name: 'My Jobs', href: '/worker/jobs', icon: Briefcase })
    }
    if (hasMobilePermission(currentUser, 'field_hours')) {
      items.splice(2, 0, { name: 'My Hours', href: '/worker/hours', icon: ClipboardList })
    }
    if (hasMobilePermission(currentUser, 'purchase_orders')) {
      items.splice(5, 0, { name: 'Purchase Orders', href: '/painter/purchase-orders', icon: FileText })
    }
    if (canAccessDefectSheetsMobile(currentUser)) {
      items.push({ name: 'Defect Sheets', href: '/jobs/defect-sheets', icon: ListChecks })
    }

    return items
  }, [user, appliesMobileProfile, forceUpdate])

  const navigation = useMemo(() => {
    if (appliesMobileProfile && mobileAdminNav) return mobileAdminNav
    return user?.role_id === 2 ? painterNav : [
    // Admin navigation (role_id = 1) – grouped: Stock section, Jobs section
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    {
      name: 'Stock',
      href: '/stock/dashboard',
      icon: Warehouse,
      submenu: [
        { name: 'Stock', href: '/stock' },
        { name: 'Suppliers', href: '/suppliers' },
        { name: 'Purchase Orders', href: '/purchase-orders' },
        { name: 'Movements', href: '/movements' },
        { name: 'Material Requests', href: '/material-requests' },
        { name: 'Monthly Restock', href: '/monthly-restocks' },
      ],
    },
    {
      name: 'Jobs',
      href: '/jobs/dashboard',
      icon: Briefcase,
      submenu: [
        { name: 'Clients', href: '/jobs/clients' },
        { name: 'Quotations', href: '/jobs/quotations' },
        { name: 'Jobs', href: '/jobs' },
        { name: 'Defect sheets', href: '/jobs/defect-sheets' },
        { name: 'Job stock usage', href: '/jobs/stock-usage' },
        { name: 'Time tracking', href: '/jobs/time-tracking' },
      ],
    },
    { name: 'User Management', href: '/users', icon: Users },
    { name: 'Alerts', href: '/alerts', icon: Bell },
  ];
  }, [user, painterNav, mobileAdminNav, appliesMobileProfile]);


  const isCurrentPath = (path) => {
    if (path === '/painter/monthly-restock') {
      return location.pathname === path || location.pathname.startsWith(`${path}/`)
    }
    if (path === '/stock') {
      // Submenu "Stock" highlights only on /stock or /stock/:id, not on /stock/dashboard
      const onStockDashboard = location.pathname === '/stock/dashboard'
      return !onStockDashboard && (location.pathname === '/stock' || location.pathname.startsWith('/stock/'))
    }
    if (path === '/jobs') {
      return location.pathname === '/jobs' || location.pathname.startsWith('/jobs/')
    }
    return location.pathname === path
  }

  // Resolve current page label for header (main nav or submenu)
  const currentPageLabel = useMemo(() => {
    for (const item of navigation) {
      if (item.submenu) {
        const sub = item.submenu.find((s) => isSubmenuItemActive(item.name, s.href, location.pathname));
        if (sub) return sub.name;
        if (isCurrentPath(item.href)) return item.name;
      } else if (isCurrentPath(item.href)) {
        return item.name;
      }
    }
    return 'Dashboard';
  }, [navigation, location.pathname]);

  // Keep parent submenu expanded while user navigates within that section
  useEffect(() => {
    setExpandedMenus((prev) => {
      let next = prev
      for (const item of navigation) {
        if (!item.submenu?.length) continue
        if (submenuSectionActive(item, location.pathname) && !prev[item.name]) {
          if (next === prev) next = { ...prev }
          next[item.name] = true
        }
      }
      return next
    })
  }, [location.pathname, navigation])
  
  // Toggle submenu (open/close on click) and always navigate to dashboard
  const openMenuAndGoToDashboard = (item) => {
    setExpandedMenus(prev => {
      const wasThisOpen = prev[item.name]
      const next = { ...prev }
      navigation.forEach(i => {
        if (i.submenu) next[i.name] = i.name === item.name ? !wasThisOpen : false
      })
      return next
    })
    navigate(item.href)
    setSidebarOpen(false)
  }

  // Real-time notifications via SSE / Pusher (window events from useSSE)
  useEffect(() => {
    let requestsTimer
    const refreshMyRequests = () => {
      clearTimeout(requestsTimer)
      requestsTimer = setTimeout(() => queryClient.invalidateQueries(['painter-material-requests']), 100)
    }
    const onAdminMaterialRequestsList = () => location.pathname === '/material-requests'
    const onPainterMyRequestsList = () => location.pathname === '/painter/my-requests'

    // Listen for new movement events from SSE
    const handleNewMovement = (event) => {
      setHasNewMovements(true)
    }

    // Listen for new material request events from SSE
    // Backend targets who receives each event (admin vs user channel). Anyone who gets the DOM event should see the dot (web + in-app browser).
    const handleNewMaterialRequest = () => {
      setHasNewMaterialRequests(true)
      refreshMyRequests()
    }

    // Status/line updates while already on the list page should not re-enable the nav dot
    // (pathname effect cleared it; these events would flip it back and feel "stuck for minutes").
    const handleMaterialRequestStatusUpdate = () => {
      refreshMyRequests()
      if (onAdminMaterialRequestsList() || onPainterMyRequestsList()) return
      setHasNewMaterialRequests(true)
    }

    const handleMaterialRequestUpdated = () => {
      refreshMyRequests()
      if (onAdminMaterialRequestsList() || onPainterMyRequestsList()) return
      setHasNewMaterialRequests(true)
    }

    // Admins receive this when a pickup/transfer is finalized (movement to review)
    const handleMaterialRequestCompleted = () => {
      setHasNewMovements(true)
    }

    // Listen for new stock alerts from SSE
    const handleNewStockAlert = (event) => {
      setHasNewAlerts(true)
    }

    // Some flows emit 'transferCompleted' instead of 'newMovement'
    const handleTransferCompleted = (event) => {
      refreshMyRequests()
      setHasNewMovements(true)
    }

    // Listen for new purchase order events from SSE
    const handleNewPurchaseOrder = () => {
      setHasNewPurchaseOrders(true)
    }

    const handlePurchaseOrderStatusUpdate = () => {
      setHasNewPurchaseOrders(true)
    }

    const handlePurchaseOrderItemsReceived = () => {
      setHasNewPurchaseOrders(true)
    }

    // Add event listeners for custom events
    window.addEventListener('newMovement', handleNewMovement)
    window.addEventListener('newMaterialRequest', handleNewMaterialRequest)
    window.addEventListener('materialRequestStatusUpdate', handleMaterialRequestStatusUpdate)
    window.addEventListener('materialRequestUpdated', handleMaterialRequestUpdated)
    window.addEventListener('newStockAlert', handleNewStockAlert)
    window.addEventListener('transferCompleted', handleTransferCompleted)
    window.addEventListener('materialRequestCompleted', handleMaterialRequestCompleted)
    window.addEventListener('newPurchaseOrder', handleNewPurchaseOrder)
    window.addEventListener('purchaseOrderStatusUpdate', handlePurchaseOrderStatusUpdate)
    window.addEventListener('purchaseOrderItemsReceived', handlePurchaseOrderItemsReceived)

    return () => {
      clearTimeout(requestsTimer)
      window.removeEventListener('newMovement', handleNewMovement)
      window.removeEventListener('newMaterialRequest', handleNewMaterialRequest)
      window.removeEventListener('materialRequestStatusUpdate', handleMaterialRequestStatusUpdate)
      window.removeEventListener('materialRequestUpdated', handleMaterialRequestUpdated)
      window.removeEventListener('newStockAlert', handleNewStockAlert)
      window.removeEventListener('transferCompleted', handleTransferCompleted)
      window.removeEventListener('materialRequestCompleted', handleMaterialRequestCompleted)
      window.removeEventListener('newPurchaseOrder', handleNewPurchaseOrder)
      window.removeEventListener('purchaseOrderStatusUpdate', handlePurchaseOrderStatusUpdate)
      window.removeEventListener('purchaseOrderItemsReceived', handlePurchaseOrderItemsReceived)
    }
  }, [user, location.pathname, queryClient])

  // Mark movements as viewed when accessing movements page
  useEffect(() => {
    if (location.pathname === '/movements') {
      localStorage.setItem('lastViewedMovements', new Date().toISOString())
      setHasNewMovements(false)
    }
  }, [location.pathname])

  // Mark material requests as viewed when accessing material requests page
  useEffect(() => {
    if (location.pathname === '/material-requests') {
      localStorage.setItem('lastViewedMaterialRequests', new Date().toISOString())
      // Nav dot for admin is driven by pending count from AdminMaterialRequests (see adminMaterialRequestsNavPending).
      return
    }
    if (location.pathname === '/painter/my-requests') {
      localStorage.setItem('lastViewedMaterialRequests', new Date().toISOString())
      setHasNewMaterialRequests(false)
    }
  }, [location.pathname])

  // Admin Material Requests page reports whether any request is still status "pending" (global count).
  useEffect(() => {
    const handler = (e) => {
      const v = e?.detail?.hasPending
      if (typeof v === 'boolean') setHasNewMaterialRequests(v)
    }
    window.addEventListener('adminMaterialRequestsNavPending', handler)
    return () => window.removeEventListener('adminMaterialRequestsNavPending', handler)
  }, [])

  // Mark alerts as viewed when accessing alerts page
  useEffect(() => {
    if (location.pathname === '/alerts' || location.pathname === '/painter/alerts') {
      localStorage.setItem('lastViewedAlerts', new Date().toISOString())
      setHasNewAlerts(false)
    }
  }, [location.pathname])

  // Mark purchase orders as viewed when accessing purchase orders page
  useEffect(() => {
    if (location.pathname === '/purchase-orders' || location.pathname === '/painter/purchase-orders') {
      localStorage.setItem('lastViewedPurchaseOrders', new Date().toISOString())
      setHasNewPurchaseOrders(false)
    }
  }, [location.pathname])

  const adminNavNotify = useMemo(() => {
    const isAdmin = user?.role_id === 1
    const stockFlags = { hasNewMovements, hasNewMaterialRequests, hasNewPurchaseOrders }
    const stockPending =
      isAdmin && (hasNewMovements || hasNewMaterialRequests || hasNewPurchaseOrders)
    const stockExpanded = !!expandedMenus['Stock']
    const jobsExpanded = !!expandedMenus['Jobs']
    return {
      isAdmin,
      stockFlags,
      // Always show parent Stock dot when anything is pending (submenu open or closed).
      // Hiding it when expanded made the UI feel "broken": only submenu showed the dot.
      showStockParentDot: stockPending,
      showJobsParentDot: isAdmin && hasNewJobsActivity && !jobsExpanded,
      stockExpanded,
      jobsExpanded,
    }
  }, [
    user?.role_id,
    hasNewMovements,
    hasNewMaterialRequests,
    hasNewPurchaseOrders,
    hasNewJobsActivity,
    expandedMenus,
  ])

  useEffect(() => {
    if (user?.role_id !== 1) return
    const onJobsNav = () => setHasNewJobsActivity(true)
    window.addEventListener('jobsNavNotification', onJobsNav)
    return () => window.removeEventListener('jobsNavNotification', onJobsNav)
  }, [user?.role_id])

  useEffect(() => {
    const onDefectSheetUpdated = (event) => {
      const detail = event?.detail || {}
      queryClient.invalidateQueries('defect-sheet-jobs-list')
      if (detail.task_status && detail.sheet_id != null) {
        queryClient.getQueriesData(['defect-sheet']).forEach(([key, cached]) => {
          if (String(cached?.data?.sheet?.id) !== String(detail.sheet_id)) return
          const updated = applyTaskStatusEvent(cached, detail)
          if (updated) queryClient.setQueryData(key, updated)
          else queryClient.invalidateQueries(key)
        })
        return
      }
      if (detail.sheet_id != null) {
        queryClient.invalidateQueries(['defect-sheet', 'id', detail.sheet_id])
      }
      if (detail.job_id != null) {
        queryClient.invalidateQueries(['defect-sheet', 'job', detail.job_id])
        queryClient.invalidateQueries(['job-defect-sheet-summary', String(detail.job_id)])
      }
    }
    window.addEventListener('defectSheetUpdated', onDefectSheetUpdated)
    return () => window.removeEventListener('defectSheetUpdated', onDefectSheetUpdated)
  }, [queryClient])

  useEffect(() => {
    if (user?.role_id !== 1) return
    if (pathClearsJobsNavDot(location.pathname)) {
      setHasNewJobsActivity(false)
    }
  }, [location.pathname, user?.role_id])

  // Listen for movementsViewed event to hide red dot
  useEffect(() => {
    const handleMovementsViewed = () => {
      setHasNewMovements(false)
    }

    const handleMaterialRequestsViewed = () => {
      setHasNewMaterialRequests(false)
    }

    const handleAlertsViewed = () => {
      setHasNewAlerts(false)
    }

    const handlePurchaseOrdersViewed = () => {
      setHasNewPurchaseOrders(false)
    }

    window.addEventListener('movementsViewed', handleMovementsViewed)
    window.addEventListener('materialRequestsViewed', handleMaterialRequestsViewed)
    window.addEventListener('alertsViewed', handleAlertsViewed)
    window.addEventListener('purchaseOrdersViewed', handlePurchaseOrdersViewed)
    
    return () => {
      window.removeEventListener('movementsViewed', handleMovementsViewed)
      window.removeEventListener('materialRequestsViewed', handleMaterialRequestsViewed)
      window.removeEventListener('alertsViewed', handleAlertsViewed)
      window.removeEventListener('purchaseOrdersViewed', handlePurchaseOrdersViewed)
    }
  }, [])

  // Fetch cron sync status (admin only)
  useEffect(() => {
    if (user?.role_id !== 1) return

    let isMounted = true
    let intervalId
    let statusInFlight = false

    const fetchSyncStatus = async () => {
      // Skip if we somehow lost the token (avoid unnecessary 403s)
      if (!localStorage.getItem('token') || document.hidden || !navigator.onLine || statusInFlight) {
        return
      }

      statusInFlight = true
      try {
        const { data } = await cronSyncAPI.getStatus()
        if (isMounted) {
          setSyncStatus(data.status)
        }
      } catch (error) {
        // Stop polling if we hit auth issues to avoid console noise
        const status = error?.response?.status
        if (status === 401 || status === 403) {
          if (intervalId) {
            clearInterval(intervalId)
            intervalId = null
          }
        }
      } finally {
        statusInFlight = false
      }
    }

    fetchSyncStatus()
    document.addEventListener('visibilitychange', fetchSyncStatus)
    intervalId = setInterval(fetchSyncStatus, 30000) // Only poll while visible and online
    
    return () => {
      document.removeEventListener('visibilitychange', fetchSyncStatus)
      isMounted = false
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [user?.role_id])

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary-50 via-primary-50 to-secondary-100 relative" style={{
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 30%, #cbd5e1 100%)',
      minHeight: '100vh'
    }}>
      {/* Background pattern — must not capture pointer events (blocks hovers/clicks on content below) */}
      <div
        className="pointer-events-none absolute inset-0 z-0 opacity-40"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23f1f5f9' fill-opacity='0.3'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
        aria-hidden
      />
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-900 bg-opacity-25" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-gradient-to-b from-gray-800 to-gray-900 shadow-xl">
          <div className="flex h-20 items-center justify-between px-4 py-4 border-b border-white/10">
            <div className="flex items-center">
                <img 
                src="/images/logos/logo_s_w.png" 
                alt="Suite" 
                className="h-10 w-auto"
                />
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav key={menuKey} className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item) => {
              const Icon = item.icon
              const hasSubmenu = item.submenu && item.submenu.length > 0
              const isExpanded = expandedMenus[item.name]
              
              // For painters, always render simple links (no submenus)
              if (user?.role_id === 2 || appliesMobileProfile || !hasSubmenu) {
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    {...navPrefetchProps(item.href)}
                    className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                      isCurrentPath(item.href)
                        ? 'bg-white text-gray-800 shadow-md'
                        : 'text-white hover:bg-gray-700 hover:text-white'
                    }`}
                    onClick={() => {
                      setSidebarOpen(false);
                    }}
                  >
                    <div className="relative">
                      <Icon className="mr-3 h-5 w-5" />
                      {item.name === 'Movements' && hasNewMovements && (
                        <div className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full animate-pulse"></div>
                      )}
                      {item.name === 'Material Requests' && hasNewMaterialRequests && (
                        <div className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full animate-pulse"></div>
                      )}
                      {item.name === 'My Requests' && (hasNewMaterialRequests || hasPickupReady) && (
                        <div className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full animate-pulse"></div>
                      )}
                      {item.name === 'Purchase Orders' && hasNewPurchaseOrders && (
                        <div className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full animate-pulse"></div>
                      )}
                      {item.name === 'Alerts' && hasNewAlerts && (
                        <div className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full animate-pulse"></div>
                      )}
                    </div>
                    {item.name}
                  </Link>
                )
              }
              
              // Admin submenu logic
              return (
                <div key={item.name}>
                  <button
                    type="button"
                    onClick={() => openMenuAndGoToDashboard(item)}
                    onMouseEnter={() => prefetchRouteChunk(item.href)}
                    className={`group flex items-center justify-between w-full px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                      isCurrentPath(item.href) || submenuSectionActive(item, location.pathname)
                        ? 'bg-white text-gray-800 shadow-md'
                        : 'text-white hover:bg-gray-700 hover:text-white'
                    }`}
                  >
                    <div className="flex items-center">
                      <div className="relative">
                        <Icon className="mr-3 h-5 w-5" />
                        {item.name === 'Stock' && adminNavNotify.showStockParentDot && <SidebarNavDot />}
                        {item.name === 'Jobs' && adminNavNotify.showJobsParentDot && <SidebarNavDot />}
                      </div>
                      {item.name}
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4 transition-transform duration-200" />
                    ) : (
                      <ChevronRight className="h-4 w-4 transition-transform duration-200" />
                    )}
                  </button>
                    {/* Submenu animation standard: overflow-hidden + transition-all duration-200 ease-in-out;
                        open: max-h-64 opacity-100; closed: max-h-0 opacity-0. Links: transition-colors duration-150. */}
                    <div
                      className={`overflow-hidden transition-all duration-200 ease-in-out ${
                        isExpanded ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'
                      }`}
                    >
                      <div className="ml-6 mt-1 space-y-1">
                        {item.submenu.map((subItem) => {
                          const showStockSubDot =
                            item.name === 'Stock' &&
                            adminNavNotify.stockExpanded &&
                            stockSubmenuHrefHasNotification(subItem.href, adminNavNotify.stockFlags)
                          const showJobsSubDot =
                            item.name === 'Jobs' &&
                            adminNavNotify.jobsExpanded &&
                            hasNewJobsActivity &&
                            jobsSubmenuHrefHasNotification(subItem.href)
                          const showSubDot = showStockSubDot || showJobsSubDot
                          return (
                          <Link
                            key={subItem.name}
                            to={subItem.href}
                            {...navPrefetchProps(subItem.href)}
                            className={`flex items-center justify-between gap-2 px-2 py-2 text-sm rounded-md transition-colors duration-150 ${
                              isSubmenuItemActive(item.name, subItem.href, location.pathname)
                                ? 'bg-white text-gray-800 shadow-md'
                                : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                            }`}
                            onClick={() => {
                              onJobsSubmenuLinkClick(subItem.href)
                              setSidebarOpen(false)
                            }}
                          >
                            <span>{subItem.name}</span>
                            {showSubDot ? <SidebarSubmenuDot /> : null}
                          </Link>
                          )
                        })}
                      </div>
                    </div>
                </div>
              )
            })}
          </nav>
          <div className="border-t border-gray-700 p-4">
            <div className="flex items-center">
              <UserAvatar user={user} sizeClass="h-8 w-8" iconClass="h-4 w-4" />
              <div className="ml-3">
                <p className="text-sm font-medium text-white">{user?.name || 'User'}</p>
                <p className="text-xs text-gray-300">
                  {displayRoleLabel}
                </p>
              </div>
            </div>
            <button
              onClick={logout}
              className="mt-3 flex w-full items-center px-2 py-2 text-sm text-white hover:bg-gray-700 hover:text-white rounded-md"
            >
              <LogOut className="mr-3 h-4 w-4" />
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-gradient-to-b from-gray-800 to-gray-900 border-r border-gray-700">
          <div className="flex h-20 items-center px-4 py-4 border-b border-white/10">
            <div className="flex items-center">
                <img 
                src="/images/logos/logo_s_w.png" 
                alt="Suite" 
                className="h-10 w-auto"
                />
            </div>
          </div>
          <nav key={menuKey} className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item) => {
              const Icon = item.icon
              const hasSubmenu = item.submenu && item.submenu.length > 0
              const isExpanded = expandedMenus[item.name]
              
              if (hasSubmenu) {
                return (
                  <div key={item.name}>
                    <button
                      type="button"
                      onClick={() => openMenuAndGoToDashboard(item)}
                      onMouseEnter={() => prefetchRouteChunk(item.href)}
                      className={`group flex items-center justify-between w-full px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                        isCurrentPath(item.href) || submenuSectionActive(item, location.pathname)
                          ? 'bg-white text-gray-800 shadow-md'
                          : 'text-white hover:bg-gray-700 hover:text-white'
                      }`}
                    >
                      <div className="flex items-center">
                        <div className="relative">
                          <Icon className="mr-3 h-5 w-5" />
                          {item.name === 'Stock' && adminNavNotify.showStockParentDot && <SidebarNavDot />}
                          {item.name === 'Jobs' && adminNavNotify.showJobsParentDot && <SidebarNavDot />}
                        </div>
                        {item.name}
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 transition-transform duration-200" />
                      ) : (
                        <ChevronRight className="h-4 w-4 transition-transform duration-200" />
                      )}
                    </button>
                    <div
                      className={`overflow-hidden transition-all duration-200 ease-in-out ${
                        isExpanded ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'
                      }`}
                    >
                      <div className="ml-6 mt-1 space-y-1">
                        {item.submenu.map((subItem) => {
                          const showStockSubDot =
                            item.name === 'Stock' &&
                            adminNavNotify.stockExpanded &&
                            stockSubmenuHrefHasNotification(subItem.href, adminNavNotify.stockFlags)
                          const showJobsSubDot =
                            item.name === 'Jobs' &&
                            adminNavNotify.jobsExpanded &&
                            hasNewJobsActivity &&
                            jobsSubmenuHrefHasNotification(subItem.href)
                          const showSubDot = showStockSubDot || showJobsSubDot
                          return (
                          <Link
                            key={subItem.name}
                            to={subItem.href}
                            {...navPrefetchProps(subItem.href)}
                            className={`flex items-center justify-between gap-2 px-2 py-2 text-sm rounded-md transition-colors duration-150 ${
                              isSubmenuItemActive(item.name, subItem.href, location.pathname)
                                ? 'bg-white text-gray-800 shadow-md'
                                : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                            }`}
                            onClick={() => onJobsSubmenuLinkClick(subItem.href)}
                          >
                            <span>{subItem.name}</span>
                            {showSubDot ? <SidebarSubmenuDot /> : null}
                          </Link>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                )
              }
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  {...navPrefetchProps(item.href)}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                    isCurrentPath(item.href)
                      ? 'bg-white text-gray-800 shadow-md'
                      : 'text-white hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  <div className="relative">
                    <Icon className="mr-3 h-5 w-5" />
                    {item.name === 'Movements' && hasNewMovements && (
                      <div className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full animate-pulse"></div>
                    )}
                    {item.name === 'Material Requests' && hasNewMaterialRequests && (
                      <div className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full animate-pulse"></div>
                    )}
                    {item.name === 'My Requests' && (hasNewMaterialRequests || hasPickupReady) && (
                      <div className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full animate-pulse"></div>
                    )}
                    {item.name === 'Purchase Orders' && hasNewPurchaseOrders && (
                      <div className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full animate-pulse"></div>
                    )}
                    {item.name === 'Alerts' && hasNewAlerts && (
                      <div className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full animate-pulse"></div>
                    )}
                  </div>
                  {item.name}
                </Link>
              )
            })}
          </nav>
          <div className="border-t border-gray-700 p-4">
            <div className="flex items-center">
              <UserAvatar user={user} sizeClass="h-8 w-8" iconClass="h-4 w-4" />
              <div className="ml-3">
                <p className="text-sm font-medium text-white">{user?.name || 'User'}</p>
                <p className="text-xs text-gray-300">
                  {displayRoleLabel}
                </p>
              </div>
            </div>
            <button
              onClick={logout}
              className="mt-3 flex w-full items-center px-2 py-2 text-sm text-white hover:bg-gray-700 hover:text-white rounded-md"
            >
              <LogOut className="mr-3 h-4 w-4" />
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Mobile header */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:hidden">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex-1 text-sm font-semibold leading-6 text-gray-900">
            {currentPageLabel}
          </div>
          {/* Sync status badge (admin only) */}
          {user?.role_id === 1 && !appliesMobileProfile && syncStatus && (
            <div className={`px-2 py-1 rounded text-xs font-medium ${
              syncStatus.isRunning 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'
            }`}>
              {syncStatus.isRunning ? '✓ Auto-Sync Active' : '✗ Sync Inactive'}
            </div>
          )}
          {/* Real-time notifications connection indicator */}
          <div className="flex items-center" title={isConnected ? 'Real-time notifications active' : 'Real-time notifications inactive - system still works normally'}>
            <div className={`w-2 h-2 rounded-full mr-2 ${isConnected ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
            <span className="text-xs text-gray-500">
              {isConnected ? 'Live' : 'Offline'}
            </span>
          </div>
        </div>

        {/* Desktop header with sync status */}
        <div className="hidden lg:sticky lg:top-0 lg:z-40 lg:flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:justify-between">
          <div className="flex-1 text-sm font-semibold leading-6 text-gray-900">
            {currentPageLabel}
          </div>
          <div className="flex items-center gap-x-4">
            {/* Sync status badge (admin only) */}
            {user?.role_id === 1 && !appliesMobileProfile && syncStatus && (
              <div className={`px-3 py-2 rounded-md text-sm font-medium ${
                syncStatus.isRunning 
                  ? 'bg-green-100 text-green-800 border border-green-300' 
                  : 'bg-red-100 text-red-800 border border-red-300'
              }`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span>{syncStatus.isRunning ? '✓ Auto-Sync' : '✗ Sync Inactive'}</span>
                  {syncStatus.isRunning && syncStatus.lastSyncTime && (
                    <span className="text-xs opacity-75 flex items-center gap-1">
                      Last: {new Date(syncStatus.lastSyncTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                      {syncStatus.lastSyncStatus === 'success' && (
                        <span className="text-green-600" title="Last sync was successful">✓</span>
                      )}
                      {syncStatus.lastSyncStatus === 'error' && (
                        <span className="text-red-600" title={`Last sync failed: ${syncStatus.lastSyncError || 'Unknown error'}`}>✗</span>
                      )}
                    </span>
                  )}
                  {syncStatus.isRunning && (syncStatus.nextSyncDisplay || syncStatus.nextSyncTime) && (
                    <span
                      className="text-xs opacity-75"
                      title={
                        syncStatus.scheduleMode === 'vercel_cron' && syncStatus.vercelCronNotes?.allSlotsUtc
                          ? `Vercel Cron (UTC): ${syncStatus.vercelCronNotes.allSlotsUtc}`
                          : undefined
                      }
                    >
                      Next: {syncStatus.nextSyncDisplay || new Date(syncStatus.nextSyncTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                    </span>
                  )}
                </div>
              </div>
            )}
            {/* Real-time notifications connection indicator */}
            <div className="flex items-center" title={isConnected ? 'Real-time notifications active' : 'Real-time notifications inactive - system still works normally'}>
              <div className={`w-2 h-2 rounded-full mr-2 ${isConnected ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
              <span className="text-xs text-gray-500">
                {isConnected ? 'Live' : 'Offline'}
              </span>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="py-6">
          <div className="mx-auto px-4 sm:px-6 lg:px-8">
            <MobileWebRouteGuard>
              {children}
            </MobileWebRouteGuard>
          </div>
        </main>
      </div>
    </div>
  )
}

export default Layout
